import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import useNotiStack from "hooks/useNotistack";
import { useProfileUsername } from "hooks/ProfileDetails";
import { useCourseGPSNameList, useTransformedHoleData } from "hooks/CourseMapDetails";
import { decryptSecretKey, getModalProperties } from "services/utils";
import ModalWrapper from "layout/ModalWrapper/ModalWrapper";
import NoificationModal from "pages/ModalPage/NoificationModal";
import BackdropLoader from "ui/BackdropLoader/BackdropLoader";
import {
  course_gps_vector_map_create,
  course_gps_vector_map_update,
  course_map_gps_mapping_details,
  course_map_gps_vector_details,
  course_mapping_gps_update,
  course_mapping_note_create,
  course_mapping_note_list,
} from "redux/CourseMapSlice";
import {
  buildDuplicatedGPSList,
  buildVectorCreateBodies,
  evaluateRawPreconditions,
} from "./duplicateBackNine";

const DuplicateBackNineModal = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id_courseFromQuery = searchParams.get("course");
  const secret_Key = decryptSecretKey();
  const username = useProfileUsername();
  const { toastSuccess, toastError, toastWarning } = useNotiStack();
  const holeWiseData = useTransformedHoleData();
  const gpsNameList = useCourseGPSNameList();
  const [submitting, setSubmitting] = useState(false);
  const modalProperties = getModalProperties();

  const user = { secretKey: secret_Key, userName: username };

  const refreshCourseData = () => {
    const body = { id_course: id_courseFromQuery };
    dispatch(course_map_gps_mapping_details({ body, user }));
    dispatch(course_map_gps_vector_details({ body, user }));
  };

  const createDuplicateNote = () => {
    dispatch(
      course_mapping_note_create({
        body: {
          note: "Duplicated holes 1-9 to 10-18",
          id_course: Number(id_courseFromQuery),
        },
        user,
      })
    )?.then((res) => {
      if (Number(res?.payload?.data?.Status) === 1) {
        dispatch(
          course_mapping_note_list({
            body: { id_course: id_courseFromQuery },
            user,
          })
        );
      } else {
        toastWarning(
          "The data was duplicated, but the mapping note could not be created."
        );
      }
    });
  };

  const buildGPSUpdateBody = (freshGPSMapping, gpsListToSend) => ({
    id_course: parseInt(id_courseFromQuery),
    clubLat: freshGPSMapping?.clubLat,
    clubLon: freshGPSMapping?.clubLon,
    GPSList: gpsListToSend,
  });

  const rollbackCopies = (vectorResults, gpsResult, freshGPSMapping, originalGPSList) => {
    const createdIds = vectorResults
      .filter((result) => result.ok && result.id)
      .map((result) => result.id);
    const rollbackRequests = createdIds.map((id) =>
      dispatch(
        course_gps_vector_map_update({
          body: { id_courseGPSVector: id, active: 0 },
          user,
        })
      )
        .then((res) => ({ ok: Number(res?.payload?.data?.Status) === 1 }))
        .catch(() => ({ ok: false }))
    );
    if (gpsResult?.ok) {
      rollbackRequests.push(
        dispatch(
          course_mapping_gps_update({
            body: buildGPSUpdateBody(freshGPSMapping, originalGPSList),
            user,
          })
        )
          .then((res) => ({ ok: Number(res?.payload?.data?.Status) === 1 }))
          .catch(() => ({ ok: false }))
      );
    }
    return Promise.all(rollbackRequests).then((results) => {
      const untracked = vectorResults.filter(
        (result) => result.ok && !result.id
      ).length;
      return results.filter((result) => !result.ok).length + untracked;
    });
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    const body = { id_course: id_courseFromQuery };
    const [vectorRes, gpsRes] = await Promise.all([
      dispatch(course_map_gps_vector_details({ body, user })),
      dispatch(course_map_gps_mapping_details({ body, user })),
    ]);
    const rawCourseData = vectorRes?.payload?.data?.vectorGPSObject;
    const freshGPSMapping = gpsRes?.payload?.data;
    if (!rawCourseData || !freshGPSMapping) {
      setSubmitting(false);
      toastError(
        "Duplicate to Back 9 aborted. Course data could not be verified, please try again."
      );
      onClose();
      return;
    }
    const preflight = evaluateRawPreconditions(
      rawCourseData,
      freshGPSMapping?.GPSList
    );
    if (!preflight.canDuplicate) {
      setSubmitting(false);
      toastError(`Duplicate to Back 9 aborted. ${preflight.reason}.`);
      onClose();
      return;
    }
    const vectorBodies = buildVectorCreateBodies(
      holeWiseData,
      parseInt(id_courseFromQuery)
    );
    const originalGPSList = freshGPSMapping?.GPSList;
    const duplicatedGPSList = buildDuplicatedGPSList(originalGPSList, gpsNameList);
    if (vectorBodies.length === 0 && !duplicatedGPSList) {
      setSubmitting(false);
      toastError("Duplicate to Back 9 failed. No data to copy.");
      onClose();
      return;
    }
    const vectorPromises = vectorBodies.map((createBody) =>
      dispatch(course_gps_vector_map_create({ body: createBody, user }))
        .then((res) => ({
          ok: Number(res?.payload?.data?.Status) === 1,
          id: res?.payload?.data?.id_courseGPSVector,
        }))
        .catch(() => ({ ok: false, id: undefined }))
    );
    const gpsPromise = duplicatedGPSList
      ? dispatch(
          course_mapping_gps_update({
            body: buildGPSUpdateBody(freshGPSMapping, duplicatedGPSList),
            user,
          })
        )
          .then((res) => ({ ok: Number(res?.payload?.data?.Status) === 1 }))
          .catch(() => ({ ok: false }))
      : Promise.resolve(null);
    const [vectorResults, gpsResult] = await Promise.all([
      Promise.all(vectorPromises),
      gpsPromise,
    ]);
    const failedVectors = vectorResults.filter((result) => !result.ok).length;
    const allOk = failedVectors === 0 && (gpsResult === null || gpsResult.ok);
    if (allOk) {
      toastSuccess("Duplicated holes 1-9 to 10-18.");
      createDuplicateNote();
      setTimeout(refreshCourseData, 500);
      onSuccess();
      return;
    }
    const leftovers = await rollbackCopies(
      vectorResults,
      gpsResult,
      freshGPSMapping,
      originalGPSList
    );
    if (leftovers === 0) {
      toastError(
        "Duplicate to Back 9 failed. All copied data was rolled back, no changes were made."
      );
    } else {
      toastError(
        `Duplicate to Back 9 failed and ${leftovers} copied object${
          leftovers === 1 ? "" : "s"
        } could not be rolled back. Remove them from holes 10-18 manually.`
      );
    }
    setTimeout(refreshCourseData, 500);
    onClose();
  };

  return (
    <>
      <BackdropLoader
        open={submitting}
        text="Duplicating holes 1-9 to 10-18..."
      />
      <ModalWrapper open={!submitting} onClose={onClose} handleClose={onClose}>
        <NoificationModal
          label="Duplicate to Back 9"
          backgroundColor={modalProperties.info.backgroundColor}
          image={modalProperties.warning.icon}
          title={`<span style='font-size:14px'>Are you sure you want to copy all mapping data<br/> from holes 1-9 to holes 10-18<br/> for course #${id_courseFromQuery}?</span>`}
          rejCallBack={onClose}
          affrCallBack={handleConfirm}
          affrBtnTxt={"Continue"}
          rejBtnTxt={"Cancel"}
        />
      </ModalWrapper>
    </>
  );
};

export default DuplicateBackNineModal;
